import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [studentFields, setStudentFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showTagsListModal, setShowTagsListModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentTags, setStudentTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [generatedReg, setGeneratedReg] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    application_status: '',
    class_id: '',
    tag_id: '',
    gender: '',
  });

  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [selectedBulkTag, setSelectedBulkTag] = useState('');
  const [selectedBulkClass, setSelectedBulkClass] = useState('');
  const [studentsByTag, setStudentsByTag] = useState([]);
  const [showTagStudentsModal, setShowTagStudentsModal] = useState(false);
  const [selectedTagForStudents, setSelectedTagForStudents] = useState(null);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showBulkResetModal, setShowBulkResetModal] = useState(false);
  const [showLoginToggleModal, setShowLoginToggleModal] = useState(false);
  const [loginEnabled, setLoginEnabled] = useState(true);

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTagStudents, setSelectedTagStudents] = useState([]);
  const [fileUploading, setFileUploading] = useState({});

  // ---------- Helper: Build Yup validation for a dynamic field ----------
  const buildDynamicFieldValidation = (field) => {
    let schema;
    const rules = field.validation_rules || {};
    const isRequired = field.is_compulsory === true;

    switch (field.field_type) {
      case 'number':
        schema = Yup.number().nullable(true);
        if (rules.min !== undefined) schema = schema.min(rules.min, `${field.display_name} must be at least ${rules.min}`);
        if (rules.max !== undefined) schema = schema.max(rules.max, `${field.display_name} must be at most ${rules.max}`);
        break;
      case 'date':
        schema = Yup.date().nullable(true);
        if (rules.min_date) schema = schema.min(new Date(rules.min_date), `${field.display_name} must be after ${rules.min_date}`);
        if (rules.max_date) schema = schema.max(new Date(rules.max_date), `${field.display_name} must be before ${rules.max_date}`);
        break;
      case 'file':
        schema = Yup.string().nullable();
        break;
      default:
        schema = Yup.string().nullable();
        if (rules.min_length) schema = schema.min(rules.min_length, `${field.display_name} must be at least ${rules.min_length} characters`);
        if (rules.max_length) schema = schema.max(rules.max_length, `${field.display_name} must be at most ${rules.max_length} characters`);
        if (rules.pattern) {
          const regex = new RegExp(rules.pattern);
          schema = schema.matches(regex, `${field.display_name} format is invalid`);
        }
        break;
    }

    if (isRequired && field.field_type !== 'file') {
      schema = schema.required(`${field.display_name} is required`);
    }

    return schema;
  };

  // ---------- Build full Yup schema (static + dynamic) ----------
  const staticStudentSchema = Yup.object().shape({
    surname: Yup.string().required('Surname is required'),
    othernames: Yup.string().required('Other names are required'),
    sex: Yup.string().oneOf(['Male', 'Female', 'Other']),
    dob: Yup.date().required('Date of birth is required'),
    application_status: Yup.string(),
    tag_id: Yup.number().required('Tag is required').nullable(),
    class_id: Yup.number().nullable(),
  });

  const dynamicValidationSchema = useMemo(() => {
    const dynamicShape = {};
    studentFields.forEach(field => {
      if (field.is_active && field.field_type !== 'file') {
        dynamicShape[field.field_code] = buildDynamicFieldValidation(field);
      } else if (field.is_active && field.field_type === 'file' && field.is_compulsory) {
        dynamicShape[field.field_code] = Yup.string().required(`${field.display_name} is required`);
      }
    });
    return staticStudentSchema.concat(Yup.object().shape({
      custom_fields: Yup.object().shape(dynamicShape)
    }));
  }, [studentFields]);

  // Tag Validation Schema
  const tagSchema = Yup.object().shape({
    tag_name: Yup.string().required('Tag name is required'),
    tag_format: Yup.string().required('Tag format is required'),
    description: Yup.string().optional(),
  });

  useEffect(() => {
    fetchData();
    fetchTags();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, fieldsRes, classesRes] = await Promise.all([
        adminAPI.getStudents(),
        adminAPI.getStudentFields(),
        adminAPI.getClasses(),
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
      setStudentFields(fieldsRes.data.filter(f => f.is_active));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const response = await adminAPI.getStudentTags();
      setStudentTags(response.data);
    } catch (error) {
      toast.error('Failed to load tags');
    } finally {
      setLoadingTags(false);
    }
  };

  // ---------- File validation before upload ----------
  const validateFileBeforeUpload = (file, field) => {
    const rules = field.validation_rules || {};
    const allowedExtensions = rules.allowed_extensions || [];
    const maxSizeMB = rules.max_size_mb || 5;
    const fileName = file.name;
    const fileExt = fileName.split('.').pop().toLowerCase();

    if (allowedExtensions.length && !allowedExtensions.includes(fileExt)) {
      toast.error(`Invalid file type for ${field.display_name}. Allowed: ${allowedExtensions.join(', ')}`);
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File too large for ${field.display_name}. Max size: ${maxSizeMB}MB`);
      return false;
    }
    return true;
  };

  // ---------- File Upload Handler with validation ----------
  const handleFileUpload = async (fieldCode, file, setFieldValue, fieldObj) => {
    if (!file) return;
    if (!validateFileBeforeUpload(file, fieldObj)) return;

    setFileUploading(prev => ({ ...prev, [fieldCode]: true }));
    try {
      const res = await adminAPI.uploadStudentFile(file);
      setFieldValue(`custom_fields.${fieldCode}`, res.data.file_url);
      toast.success('File uploaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setFileUploading(prev => ({ ...prev, [fieldCode]: false }));
    }
  };

  // ---------- CSV Export Utility ----------
  const exportToCSV = (data, filename, headers, tagInfo = null) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    let csvContent = '';
    if (tagInfo) {
      csvContent += `Tag Name,${tagInfo.tag_name}\n`;
      csvContent += `Format,${tagInfo.tag_format}\n`;
      csvContent += `Current Counter,${tagInfo.current_counter}\n`;
      csvContent += `Total Students,${data.length}\n`;
      csvContent += '\n';
    }
    csvContent += headers.join(',') + '\n';
    data.forEach(item => {
      const row = headers.map(header => {
        let value = item[header.toLowerCase()] || '';
        if (header === 'Registration') value = item.student_reg;
        if (header === 'Name') value = item.full_name || `${item.surname || ''} ${item.othernames || ''}`.trim();
        if (header === 'Status') value = item.application_status || '';
        if (header === 'Created') value = item.created_at ? format(new Date(item.created_at), 'dd/MM/yyyy') : 'N/A';
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
      csvContent += row + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTagStudents = async (tag) => {
    try {
      setLoadingTags(true);
      const response = await adminAPI.getStudentsByTag(tag.id);
      const students = response.data;
      if (!students || students.length === 0) {
        toast.error('No students found for this tag');
        return;
      }
      const filename = `students_${tag.tag_name.replace(/\s+/g, '_')}.csv`;
      const headers = ['Registration', 'Name', 'Gender', 'Status', 'Created'];
      exportToCSV(students, filename, headers, tag);
      toast.success('Export started');
    } catch (error) {
      toast.error('Failed to export students');
    } finally {
      setLoadingTags(false);
    }
  };

  const handleBulkLoginToggle = async () => {
    try {
      setLoading(true);
      await adminAPI.toggleStudentLoginBulk({ enabled: loginEnabled });
      toast.success(loginEnabled ? 'Student login enabled successfully' : 'Student login disabled successfully');
      setShowLoginToggleModal(false);
    } catch (error) {
      toast.error('Failed to update login status');
    } finally {
      setLoading(false);
    }
  };

  const massPassReset = async () => {
    if (!defaultPassword || defaultPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (defaultPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoadingTags(true);
      await adminAPI.changePasswordBulkStudent({ default_password: defaultPassword });
      toast.success('All student passwords have been reset');
      setShowBulkResetModal(false);
      setDefaultPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to reset passwords');
    } finally {
      setLoadingTags(false);
    }
  };

  const handleBulkApprove = async (studentList, source = 'main') => {
    const regs = studentList.map(s => (typeof s === 'string' ? s : s.student_reg));
    if (regs.length === 0) {
      toast.error('No students selected');
      return;
    }
    try {
      setLoading(true);
      await adminAPI.bulkApproveStudents(regs);
      toast.success(`${regs.length} student(s) approved successfully`);
      if (source === 'main') setSelectedStudents([]);
      else setSelectedTagStudents([]);
      await fetchData();
      if (source === 'tag' && selectedTagForStudents) {
        const response = await adminAPI.getStudentsByTag(selectedTagForStudents.id);
        setStudentsByTag(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk approval failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReject = async (studentList, source = 'main') => {
    const regs = studentList.map(s => (typeof s === 'string' ? s : s.student_reg));
    if (regs.length === 0) {
      toast.error('No students selected');
      return;
    }
    const reason = prompt('Enter rejection reason for all selected students:');
    if (!reason) return;
    try {
      setLoading(true);
      await adminAPI.bulkRejectStudents(regs, reason);
      toast.success(`${regs.length} student(s) rejected successfully`);
      if (source === 'main') setSelectedStudents([]);
      else setSelectedTagStudents([]);
      await fetchData();
      if (source === 'tag' && selectedTagForStudents) {
        const response = await adminAPI.getStudentsByTag(selectedTagForStudents.id);
        setStudentsByTag(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk rejection failed');
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (filteredList) => {
    if (selectedStudents.length === filteredList.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredList.map(s => s.student_reg));
    }
  };

  const handleSelectStudent = (reg) => {
    setSelectedStudents(prev =>
      prev.includes(reg) ? prev.filter(r => r !== reg) : [...prev, reg]
    );
  };

  const handleSelectAllTag = (list) => {
    if (selectedTagStudents.length === list.length) {
      setSelectedTagStudents([]);
    } else {
      setSelectedTagStudents(list.map(s => s.student_reg));
    }
  };

  const handleSelectTagStudent = (reg) => {
    setSelectedTagStudents(prev =>
      prev.includes(reg) ? prev.filter(r => r !== reg) : [...prev, reg]
    );
  };

  const handleCreateStudent = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingStudent) {
        await adminAPI.updateStudent(editingStudent.student_reg, values);
        toast.success('Student updated successfully');
      } else {
        if (!values.tag_id) {
          toast.error('Please select a tag to generate registration number');
          setSubmitting(false);
          return;
        }
        const regResponse = await adminAPI.generateStudentReg({
          tag_id: values.tag_id,
          year: new Date().getFullYear(),
          class_name: values.class_id ? classes.find(c => c.id === values.class_id)?.class_name || '' : ''
        });
        const studentData = {
          ...values,
          student_reg: regResponse.data.registration_number
        };
        await adminAPI.createStudent(studentData);
        toast.success('Student created successfully');
      }
      setShowModal(false);
      resetForm();
      setEditingStudent(null);
      setGeneratedReg('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTag = async (values, { setSubmitting, resetForm }) => {
    try {
      await adminAPI.createStudentTag(values);
      toast.success('Tag created successfully');
      setShowTagModal(false);
      resetForm();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateReg = async (tagId, classId) => {
    try {
      const className = classId ? classes.find(c => c.id === classId)?.class_name || '' : '';
      const response = await adminAPI.generateStudentReg({
        tag_id: tagId,
        year: new Date().getFullYear(),
        class_name: className,
      });
      setGeneratedReg(response.data.registration_number);
      toast.success('Registration number generated');
    } catch (error) {
      toast.error('Failed to generate registration number');
    }
  };

  const handleViewStudentsByTag = async (tag) => {
    try {
      setSelectedTagForStudents(tag);
      const response = await adminAPI.getStudentsByTag(tag.id);
      setStudentsByTag(response.data);
      setSelectedTagStudents([]);
      setShowTagStudentsModal(true);
    } catch (error) {
      console.log(error);
      toast.error('Failed to fetch students by tag');
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setSelectedTag(student.tag_id || '');
    setGeneratedReg(student.student_reg);
    setShowModal(true);
  };

  const handleDeleteStudent = async (studentReg) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await adminAPI.deleteStudent(studentReg);
        toast.success('Student deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete student');
      }
    }
  };

  const handleApproveStudent = async (studentReg) => {
    try {
      await adminAPI.approveStudent(studentReg);
      toast.success('Student approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve student');
    }
  };

  const handleRejectStudent = async (studentReg) => {
    const reason = prompt('Please enter rejection reason:');
    if (reason) {
      try {
        await adminAPI.rejectStudent(studentReg, { reason });
        toast.success('Student rejected successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reject student');
      }
    }
  };

  const handleStatusToggle = async (studentReg, currentStatus) => {
    if (currentStatus === 'pending') {
      await handleApproveStudent(studentReg);
    } else if (currentStatus === 'approved') {
      await handleRejectStudent(studentReg);
    } else {
      await handleApproveStudent(studentReg);
    }
  };

  const handleBulkImport = async () => {
    if (!uploadFile || !selectedBulkTag) {
      toast.error('Please select a file and tag');
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('tag_id', selectedBulkTag);
    if (selectedBulkClass) {
      formData.append('class_id', selectedBulkClass);
    }
    try {
      setLoading(true);
      const response = await adminAPI.bulkCreateStudents(formData);
      setImportResults({
        ...response.data,
        total: response.data.created_count || response.data.created?.length || 0,
        errors: response.data.errors || []
      });
      toast.success(response.data.message);
      fetchData();
      setUploadFile(null);
      setSelectedBulkTag('');
      setSelectedBulkClass('');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
        setImportResults({
          message: errorDetail,
          errors: [errorDetail],
          created_count: 0,
          total: 0
        });
      } else if (errorDetail && typeof errorDetail === 'object') {
        toast.error(errorDetail.message || 'Import failed');
        setImportResults({
          message: errorDetail.message || 'Import failed',
          errors: Array.isArray(errorDetail.errors) ? errorDetail.errors : [String(errorDetail)],
          created_count: errorDetail.created_count || 0,
          total: errorDetail.total || 0
        });
      } else {
        toast.error('Import failed');
        setImportResults({
          message: 'Import failed',
          errors: ['Unknown error occurred'],
          created_count: 0,
          total: 0
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await adminAPI.downloadStudentTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'student_bulk_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.student_reg.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.othernames.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${student.surname} ${student.othernames}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filters.application_status || student.application_status === filters.application_status;
    const matchesTag = !filters.tag_id || student.tag_id === parseInt(filters.tag_id);
    const matchesGender = !filters.gender || student.sex === filters.gender;
    return matchesSearch && matchesStatus && matchesTag && matchesGender;
  });

  const getStatusBadge = (status) => {
    const statusColors = {
      'approved': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'rejected': 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getGenderBadge = (gender) => {
    const genderColors = {
      'Male': 'bg-blue-100 text-blue-800',
      'Female': 'bg-pink-100 text-pink-800',
      'Other': 'bg-purple-100 text-purple-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${genderColors[gender] || 'bg-gray-100 text-gray-800'}`}>
        {gender}
      </span>
    );
  };

  const getTagName = (tagId) => {
    const tag = studentTags.find(t => t.id === tagId);
    return tag ? tag.tag_name : 'No Tag';
  };

  const getStatusActionText = (status) => {
    switch (status) {
      case 'approved': return 'Reject';
      case 'pending': return 'Approve';
      case 'rejected': return 'Approve';
      default: return 'Approve';
    }
  };

  if (loading && !showModal && !showTagModal && !showBulkImportModal) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Management</h1>
          <p className="text-gray-600">Manage student records and applications</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={() => setShowBulkResetModal(true)} className="btn-secondary flex items-center">
            <i className="fas fa-unlock-alt mr-2"></i> Pass. Reset
          </button>
          <button onClick={() => setShowLoginToggleModal(true)} className="btn-secondary flex items-center">
            <i className="fas fa-sliders-h mr-2"></i> Config
          </button>
          <button onClick={() => setShowTagsListModal(true)} className="btn-secondary flex items-center">
            <i className="fas fa-tags mr-2"></i> View Tags
          </button>
          <button onClick={() => setShowTagModal(true)} className="btn-secondary flex items-center">
            <i className="fas fa-tag mr-2"></i> Create Tag
          </button>
          <button
            onClick={() => {
              setEditingStudent(null);
              setSelectedTag('');
              setGeneratedReg('');
              setShowModal(true);
            }}
            className="btn-primary flex items-center"
          >
            <i className="fas fa-user-plus mr-2"></i> Add Student
          </button>
          <button onClick={() => setShowBulkImportModal(true)} className="btn-primary flex items-center">
            <i className="fas fa-file-upload mr-2"></i> Bulk Import
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-2xl font-bold">{students.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <i className="fas fa-users text-primary-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Approved</p>
              <p className="text-2xl font-bold">
                {students.filter(s => s.application_status === 'approved').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold">
                {students.filter(s => s.application_status === 'pending').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <i className="fas fa-clock text-yellow-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Dynamic Fields</p>
              <p className="text-2xl font-bold">{studentFields.length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-list text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Tags</p>
              <p className="text-2xl font-bold">{studentTags.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-tag text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search by name or registration"
                className="pl-10 input-field"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select
              className="input-field"
              value={filters.application_status}
              onChange={(e) => setFilters({...filters, application_status: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag Filter</label>
            <select
              className="input-field"
              value={filters.tag_id}
              onChange={(e) => setFilters({...filters, tag_id: e.target.value})}
            >
              <option value="">All Tags</option>
              {studentTags.map(tag => (
                <option key={tag.id} value={tag.id}>{tag.tag_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender Filter</label>
            <select
              className="input-field"
              value={filters.gender}
              onChange={(e) => setFilters({...filters, gender: e.target.value})}
            >
              <option value="">All Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedStudents.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-blue-800 font-medium">
            {selectedStudents.length} student(s) selected
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => handleBulkApprove(selectedStudents, 'main')}
              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center"
              disabled={loading}
            >
              <i className="fas fa-check-circle mr-1"></i> Approve Selected
            </button>
            <button
              onClick={() => handleBulkReject(selectedStudents, 'main')}
              className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center"
              disabled={loading}
            >
              <i className="fas fa-times-circle mr-1"></i> Reject Selected
            </button>
            <button
              onClick={() => setSelectedStudents([])}
              className="px-3 py-1 border border-gray-300 bg-white rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                    onChange={() => handleSelectAll(filteredStudents)}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student.student_reg} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={selectedStudents.includes(student.student_reg)}
                      onChange={() => handleSelectStudent(student.student_reg)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-user-graduate text-green-600"></i>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.surname} {student.othernames}
                        </div>
                        <div className="text-sm text-gray-500">{student.student_reg}</div>
                        {student.tag_id && (
                          <div className="text-xs mt-1">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                              Tag: {getTagName(student.tag_id)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                   {/* </div> */}
                   </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-gray-500">Gender: </span>
                        {getGenderBadge(student.sex)}
                      </div>
                      <div className="text-sm text-gray-500">
                        DOB: {student.dob ? format(new Date(student.dob), 'dd/MM/yyyy') : 'N/A'}
                      </div>
                    </div>
                  </td>
                   {/* </div> */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(student.application_status)}
                      <button
                        onClick={() => handleStatusToggle(student.student_reg, student.application_status)}
                        className={`text-xs px-2 py-1 rounded ${
                          student.application_status === 'approved'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {getStatusActionText(student.application_status)}
                      </button>
                    </div>
                   </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {student.created_at ? format(new Date(student.created_at), 'dd/MM/yyyy') : 'N/A'}
                   </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button onClick={() => handleEdit(student)} className="text-primary-600 hover:text-primary-900">
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button onClick={() => handleDeleteStudent(student.student_reg)} className="text-red-600 hover:text-red-900">
                      <i className="fas fa-trash"></i> Delete
                    </button>
                    {student.tag_id && (
                      <button
                        onClick={() => {
                          const tag = studentTags.find(t => t.id === student.tag_id);
                          if (tag) handleViewStudentsByTag(tag);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <i className="fas fa-users"></i> View Tag
                      </button>
                    )}
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-user-graduate text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No students found</p>
            {searchTerm && <p className="text-sm text-gray-400 mt-1">Try a different search term</p>}
          </div>
        )}
      </div>

      {/* Create/Edit Student Modal with Dynamic Validation */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStudent(null);
          setGeneratedReg('');
          setSelectedTag('');
        }}
        title={editingStudent ? 'Edit Student' : 'Add New Student'}
        size="lg"
      >
        <Formik
          initialValues={{
            surname: editingStudent?.surname || '',
            othernames: editingStudent?.othernames || '',
            sex: editingStudent?.sex || 'Male',
            dob: editingStudent?.dob ? format(new Date(editingStudent.dob), 'yyyy-MM-dd') : '',
            application_status: editingStudent?.application_status || 'pending',
            tag_id: editingStudent?.tag_id || selectedTag || '',
            class_id: editingStudent?.class_id || '',
            custom_fields: editingStudent?.custom_fields || {},
          }}
          validationSchema={dynamicValidationSchema}
          onSubmit={handleCreateStudent}
          enableReinitialize
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => (
            <Form className="space-y-4">
              {/* Tag Selection for Registration Generation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Tag *
                </label>
                <div className="flex space-x-2">
                  <Field
                    as="select"
                    name="tag_id"
                    className="input-field flex-1"
                    required
                    onChange={(e) => {
                      setFieldValue('tag_id', e.target.value);
                      setSelectedTag(e.target.value);
                    }}
                  >
                    <option value="">Select Tag for Registration</option>
                    {studentTags.map(tag => (
                      <option key={tag.id} value={tag.id}>
                        {tag.tag_name} ({tag.tag_format})
                      </option>
                    ))}
                  </Field>
                  {values.tag_id && !editingStudent && (
                    <button
                      type="button"
                      onClick={() => handleGenerateReg(values.tag_id, values.class_id)}
                      className="btn-secondary whitespace-nowrap"
                    >
                      <i className="fas fa-sync-alt mr-1"></i> Generate Reg
                    </button>
                  )}
                </div>
                {generatedReg && !editingStudent && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Generated Registration:</strong> {generatedReg}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      This registration number will be assigned to the student
                    </p>
                  </div>
                )}
                {editingStudent && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-sm text-gray-800">
                      <strong>Current Registration:</strong> {editingStudent.student_reg}
                    </p>
                  </div>
                )}
                {errors.tag_id && touched.tag_id && (
                  <div className="text-red-500 text-sm mt-1">{errors.tag_id}</div>
                )}
              </div>

              {/* Student Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surname *</label>
                  <Field type="text" name="surname" className="input-field" placeholder="Enter surname" />
                  {errors.surname && touched.surname && <div className="text-red-500 text-sm mt-1">{errors.surname}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Names *</label>
                  <Field type="text" name="othernames" className="input-field" placeholder="Enter other names" />
                  {errors.othernames && touched.othernames && <div className="text-red-500 text-sm mt-1">{errors.othernames}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                  <Field as="select" name="sex" className="input-field">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <Field type="date" name="dob" className="input-field" />
                  {errors.dob && touched.dob && <div className="text-red-500 text-sm mt-1">{errors.dob}</div>}
                </div>
              </div>

              {/* Class Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class (Optional - for registration format)
                </label>
                <Field as="select" name="class_id" className="input-field">
                  <option value="">No Class</option>
                  {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.class_name}</option>)}
                </Field>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Status</label>
                <Field as="select" name="application_status" className="input-field">
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Field>
              </div>

              {/* Dynamic Fields with File Upload Support and Validation */}
              {studentFields.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                  <div className="space-y-3 border border-gray-200 rounded-lg p-4">
                    {studentFields.map(field => {
                      const fieldPath = `custom_fields.${field.field_code}`;
                      const error = errors.custom_fields?.[field.field_code];
                      const touchedField = touched.custom_fields?.[field.field_code];
                      const fieldObj = field;

                      // File upload field with client-side validation
                      if (field.field_type === 'file') {
                        return (
                          <div key={field.id}>
                            <label className="block text-sm text-gray-700 mb-1">
                              {field.display_name} {field.is_compulsory && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="file"
                              accept={(() => {
                                const rules = field.validation_rules || {};
                                const exts = rules.allowed_extensions || [];
                                if (exts.length) return exts.map(ext => `.${ext}`).join(',');
                                return 'image/*,application/pdf';
                              })()}
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) await handleFileUpload(field.field_code, file, setFieldValue, field);
                              }}
                              className="input-field"
                            />
                            {fileUploading[field.field_code] && (
                              <p className="text-sm text-blue-500 mt-1">
                                <i className="fas fa-spinner fa-spin mr-1"></i> Uploading...
                              </p>
                            )}
                            {values.custom_fields?.[field.field_code] && (
                              <div className="mt-1">
                                <a
                                  href={`https://multi-schoolsuite.cbthost.com${values.custom_fields[field.field_code]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 text-sm"
                                >
                                  <i className="fas fa-file-alt mr-1"></i> View uploaded file
                                </a>
                              </div>
                            )}
                            {error && touchedField && <div className="text-red-500 text-sm mt-1">{error}</div>}
                          </div>
                        );
                      }

                      // Select field
                      if (field.field_type === 'select' && field.options) {
                        return (
                          <div key={field.id}>
                            <label className="block text-sm text-gray-700 mb-1">
                              {field.display_name} {field.is_compulsory && <span className="text-red-500">*</span>}
                            </label>
                            <Field as="select" name={fieldPath} className="input-field">
                              <option value="">Select {field.display_name}</option>
                              {field.options.split(',').map(option => (
                                <option key={option.trim()} value={option.trim()}>{option.trim()}</option>
                              ))}
                            </Field>
                            {error && touchedField && <div className="text-red-500 text-sm mt-1">{error}</div>}
                          </div>
                        );
                      }

                      // Textarea field
                      if (field.field_type === 'textarea') {
                        return (
                          <div key={field.id}>
                            <label className="block text-sm text-gray-700 mb-1">
                              {field.display_name} {field.is_compulsory && <span className="text-red-500">*</span>}
                            </label>
                            <Field as="textarea" name={fieldPath} className="input-field" rows="3" />
                            {error && touchedField && <div className="text-red-500 text-sm mt-1">{error}</div>}
                          </div>
                        );
                      }

                      // Boolean (checkbox)
                      if (field.field_type === 'boolean') {
                        return (
                          <div key={field.id} className="flex items-center">
                            <Field type="checkbox" name={fieldPath} className="mr-2" />
                            <label className="text-sm text-gray-700">{field.display_name}</label>
                          </div>
                        );
                      }

                      // Text, Number, Date with dynamic Yup validation already applied
                      return (
                        <div key={field.id}>
                          <label className="block text-sm text-gray-700 mb-1">
                            {field.display_name} {field.is_compulsory && <span className="text-red-500">*</span>}
                          </label>
                          <Field
                            type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                            name={fieldPath}
                            className="input-field"
                          />
                          {error && touchedField && <div className="text-red-500 text-sm mt-1">{error}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingStudent(null);
                    setGeneratedReg('');
                    setSelectedTag('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn-primary px-4 py-2">
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      {editingStudent ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingStudent ? 'Update Student' : 'Create Student'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Create Tag Modal */}
      <Modal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        title="Create Student Tag"
        size="md"
      >
        <Formik
          initialValues={{ tag_name: '', tag_format: '', description: '' }}
          validationSchema={tagSchema}
          onSubmit={handleCreateTag}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name *</label>
                <Field type="text" name="tag_name" className="input-field" placeholder="e.g., ETS Tag" />
                {errors.tag_name && touched.tag_name && <div className="text-red-500 text-sm mt-1">{errors.tag_name}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Format *</label>
                <Field type="text" name="tag_format" className="input-field" placeholder="e.g., ETS/{year}/{class}/{seq:04d}" />
                <p className="text-xs text-gray-500 mt-1">
                  Use placeholders: {"{year}"}, {"{class_name}"}, {"{seq}"} (with formatting like {"{seq:04d}"})
                </p>
                {errors.tag_format && touched.tag_format && <div className="text-red-500 text-sm mt-1">{errors.tag_format}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Field as="textarea" name="description" className="input-field" rows="3" placeholder="Optional description" />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowTagModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="btn-primary px-4 py-2">
                  {isSubmitting ? <><i className="fas fa-spinner fa-spin mr-2"></i>Creating...</> : <><i className="fas fa-save mr-2"></i>Create Tag</>}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Tags List Modal */}
      <Modal
        isOpen={showTagsListModal}
        onClose={() => setShowTagsListModal(false)}
        title="Student Tags"
        size="lg"
      >
        {loadingTags ? (
          <div className="flex justify-center py-8"><div className="loading-spinner"></div></div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Counter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {studentTags.map(tag => (
                    <tr key={tag.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{tag.tag_name}</div>
                        {tag.description && <div className="text-sm text-gray-500">{tag.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <code className="bg-gray-100 px-2 py-1 rounded">{tag.tag_format}</code>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{tag.current_counter}</span>
                      </td>
                      <td className="px-4 py-3 text-sm space-x-2">
                        <button onClick={() => handleGenerateReg(tag.id)} className="text-primary-600 hover:text-primary-900">
                          <i className="fas fa-barcode mr-1"></i> Generate
                        </button>
                        <button onClick={() => handleViewStudentsByTag(tag)} className="text-green-600 hover:text-green-900">
                          <i className="fas fa-users mr-1"></i> View
                        </button>
                        <button
                          onClick={() => handleExportTagStudents(tag)}
                          className="text-blue-600 hover:text-blue-900"
                          disabled={loadingTags}
                        >
                          <i className="fas fa-download mr-1"></i> Export
                        </button>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {studentTags.length === 0 && (
              <div className="text-center py-8">
                <i className="fas fa-tags text-3xl text-gray-300 mb-2"></i>
                <p className="text-gray-500">No tags created yet</p>
                <button onClick={() => { setShowTagsListModal(false); setShowTagModal(true); }} className="mt-2 text-primary-600 hover:text-primary-800">Create your first tag</button>
              </div>
            )}
            <div className="flex justify-end pt-4">
              <button onClick={() => setShowTagsListModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Close</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Reset Modal */}
      <Modal
        isOpen={showBulkResetModal}
        onClose={() => setShowBulkResetModal(false)}
        title="Bulk Reset Student Passwords"
        size="md"
      >
        <div className="space-y-5">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex gap-3">
            <i className="fas fa-exclamation-triangle text-red-600 text-lg"></i>
            <p className="text-sm text-red-700">This will reset <strong>ALL student passwords</strong>.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">New Default Password</label>
            <input type="password" value={defaultPassword} onChange={(e) => setDefaultPassword(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="Enter new default password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="Confirm password" />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button onClick={() => setShowBulkResetModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
            <button disabled={loadingTags} onClick={massPassReset} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              {loadingTags ? 'Resetting...' : 'Reset Passwords'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Login Toggle Modal */}
      <Modal
        isOpen={showLoginToggleModal}
        onClose={() => setShowLoginToggleModal(false)}
        title="Student Login Control"
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex gap-3">
            <i className="fas fa-user-lock text-blue-600 text-lg"></i>
            <p className="text-sm text-blue-700">
              This action will <strong>{loginEnabled ? 'ENABLE' : 'DISABLE'}</strong> login access for <strong>ALL students</strong>.
            </p>
          </div>
          <div className="flex items-center justify-between border rounded-lg p-4">
            <div>
              <p className="font-medium text-gray-800">Student Login Access</p>
              <p className="text-sm text-gray-500">Control whether students can log in</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only" checked={loginEnabled} onChange={() => setLoginEnabled(!loginEnabled)} />
              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-600 relative">
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
              </div>
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button onClick={() => setShowLoginToggleModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={handleBulkLoginToggle} disabled={loading} className={`px-4 py-2 rounded-lg text-white ${loginEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
              {loading ? 'Updating...' : loginEnabled ? 'Enable Login' : 'Disable Login'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Tag Students Modal with Bulk Actions */}
      <Modal
        isOpen={showTagStudentsModal}
        onClose={() => {
          setShowTagStudentsModal(false);
          setStudentsByTag([]);
          setSelectedTagForStudents(null);
          setSelectedTagStudents([]);
        }}
        title={selectedTagForStudents ? `Students with Tag: ${selectedTagForStudents.tag_name}` : 'Tag Students'}
        size="xl"
      >
        <div className="space-y-4">
          {selectedTagStudents.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-blue-800 font-medium">{selectedTagStudents.length} student(s) selected</span>
              <div className="flex space-x-2">
                <button onClick={() => handleBulkApprove(selectedTagStudents, 'tag')} className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center" disabled={loading}>
                  <i className="fas fa-check-circle mr-1"></i> Approve Selected
                </button>
                <button onClick={() => handleBulkReject(selectedTagStudents, 'tag')} className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center" disabled={loading}>
                  <i className="fas fa-times-circle mr-1"></i> Reject Selected
                </button>
                <button onClick={() => setSelectedTagStudents([])} className="px-3 py-1 border border-gray-300 bg-white rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Clear</button>
              </div>
            </div>
          )}

          {selectedTagForStudents && studentsByTag.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  const filename = `students_${selectedTagForStudents.tag_name.replace(/\s+/g, '_')}.csv`;
                  const headers = ['Registration', 'Name', 'Gender', 'Status', 'Created'];
                  exportToCSV(studentsByTag, filename, headers, selectedTagForStudents);
                }}
                className="btn-secondary flex items-center text-sm"
              >
                <i className="fas fa-download mr-2"></i> Export to Excel
              </button>
            </div>
          )}

          {selectedTagForStudents && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Tag Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-600">Tag Name:</p><p className="font-medium">{selectedTagForStudents.tag_name}</p></div>
                <div><p className="text-sm text-gray-600">Format:</p><code className="text-sm bg-gray-100 px-2 py-1 rounded">{selectedTagForStudents.tag_format}</code></div>
                <div><p className="text-sm text-gray-600">Current Counter:</p><p className="font-medium">{selectedTagForStudents.current_counter}</p></div>
                <div><p className="text-sm text-gray-600">Total Students:</p><p className="font-medium">{studentsByTag.length}</p></div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={selectedTagStudents.length === studentsByTag.length && studentsByTag.length > 0}
                      onChange={() => handleSelectAllTag(studentsByTag)}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentsByTag.map((student) => (
                  <tr key={student.id}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedTagStudents.includes(student.student_reg)}
                        onChange={() => handleSelectTagStudent(student.student_reg)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.student_reg}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{student.full_name}</div>
                      <div className="text-sm text-gray-500">{student.sex}</div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(student.application_status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {student.created_at ? format(new Date(student.created_at), 'dd/MM/yyyy') : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {studentsByTag.length === 0 && (
            <div className="text-center py-8">
              <i className="fas fa-users text-3xl text-gray-300 mb-2"></i>
              <p className="text-gray-500">No students found with this tag</p>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={() => {
                setShowTagStudentsModal(false);
                setStudentsByTag([]);
                setSelectedTagForStudents(null);
                setSelectedTagStudents([]);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={showBulkImportModal}
        onClose={() => {
          setShowBulkImportModal(false);
          setUploadFile(null);
          setImportResults(null);
          setSelectedBulkTag('');
          setSelectedBulkClass('');
        }}
        title="Bulk Student Import"
        size="lg"
      >
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2"><i className="fas fa-info-circle mr-2"></i>Excel Template Instructions</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Download the template below for correct format</li>
              <li>• Required columns: surname, othernames, sex, dob</li>
              <li>• Optional columns: application_status, custom fields</li>
              <li>• Registration numbers will be auto-generated from selected tag</li>
              <li>• Class selection is optional for registration format</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Excel File *</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setUploadFile(e.target.files[0])} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploadFile ? (
                  <div className="text-green-600">
                    <i className="fas fa-file-excel text-2xl mb-2"></i>
                    <p className="font-medium">{uploadFile.name}</p>
                    <p className="text-sm text-gray-500">{(uploadFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                ) : (
                  <div>
                    <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                    <p className="text-gray-600">Click to upload Excel file</p>
                    <p className="text-sm text-gray-500">.xlsx, .xls, or .csv files</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Tag *</label>
              <select className="input-field" value={selectedBulkTag} onChange={(e) => setSelectedBulkTag(e.target.value)} required>
                <option value="">Select Tag</option>
                {studentTags.map(tag => <option key={tag.id} value={tag.id}>{tag.tag_name} ({tag.tag_format})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Class (Optional)</label>
              <select className="input-field" value={selectedBulkClass} onChange={(e) => setSelectedBulkClass(e.target.value)}>
                <option value="">No Class</option>
                {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.class_name}</option>)}
              </select>
            </div>
          </div>

          {importResults && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-3">Import Results</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between"><span className="text-gray-600">Total Processed:</span><span className="font-medium">{importResults.total || 0}</span></div>
                <div className="flex items-center justify-between text-green-600"><span>Successfully Created:</span><span className="font-medium">{importResults.created_count || 0}</span></div>
                {importResults.created && importResults.created.length > 0 && (
                  <div>
                    <p className="text-green-600 mb-2">Created Registrations ({importResults.created.length}):</p>
                    <div className="max-h-32 overflow-y-auto text-sm bg-gray-100 p-2 rounded">
                      {importResults.created.map((reg, index) => <div key={index} className="text-gray-600 mb-1">• {reg}</div>)}
                    </div>
                  </div>
                )}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div>
                    <p className="text-red-600 mb-2">Errors ({importResults.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto text-sm bg-red-50 p-2 rounded">
                      {importResults.errors.map((error, index) => <div key={index} className="text-red-500 mb-1">• {String(error)}</div>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <button onClick={handleDownloadTemplate} className="btn-secondary flex items-center">
              <i className="fas fa-download mr-2"></i> Download Template
            </button>
            <div className="space-x-3">
              <button onClick={() => setShowBulkImportModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleBulkImport} disabled={!uploadFile || !selectedBulkTag || loading} className="btn-primary px-4 py-2">
                {loading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Importing...</> : <><i className="fas fa-upload mr-2"></i>Import Students</>}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentManagement;